import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { usePlatformChatSession } from '@/hooks/usePlatformChatSession';

type ChatState = 'closed' | 'horizontal' | 'expanded';

type AgentChatContextType = ReturnType<typeof usePlatformChatSession> & {
  chatState: ChatState;
  setChatState: (state: ChatState) => void;
  openChat: () => void;
  closeChat: () => void;
  expandChat: () => void;
  sendMessage: ReturnType<typeof usePlatformChatSession>['requestSend'];
};

const AgentChatContext = createContext<AgentChatContextType | undefined>(undefined);

export const AgentChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const session = usePlatformChatSession();

  const [chatState, setChatState] = useState<ChatState>(() => {
    try {
      const saved = localStorage.getItem('agent-chat-state');
      return saved ? JSON.parse(saved) : 'closed';
    } catch {
      return 'closed';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('agent-chat-state', JSON.stringify(chatState));
    } catch {
      // quota exceeded
    }
  }, [chatState]);

  const openChat = useCallback(() => setChatState('expanded'), []);
  const closeChat = useCallback(() => setChatState('closed'), []);
  const expandChat = useCallback(() => setChatState('expanded'), []);

  const contextValue = useMemo(
    () => ({
      ...session,
      chatState,
      setChatState,
      openChat,
      closeChat,
      expandChat,
      sendMessage: session.requestSend,
    }),
    [chatState, openChat, closeChat, expandChat, session]
  );

  return <AgentChatContext.Provider value={contextValue}>{children}</AgentChatContext.Provider>;
};

export const useAgentChat = () => {
  const context = useContext(AgentChatContext);
  if (context === undefined) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
};
